/**
 * Line Detection Worker Manager
 *
 * Client for communicating with two Web Workers:
 * 1. Line detection processor worker — line detection + polygon processing
 * 2. Sampling preview worker — depth segmentation + hover preview masks
 *
 * The split ensures hover preview remains responsive even during heavy polygon processing.
 */

import { LineDetectionKwargs } from './LineDetectionPipeline';
import { PipelineConfig } from '../../../../utils/BasePipeline';
import type { WorkerRequest, WorkerResponse, ProcessedRegionPolygon } from '../../../../utils/workers/line-detection-processor.worker';
import type { SamplingPreviewResponse, DepthAutoPrompt, RegionGraph } from '../../../../utils/workers/sampling-preview.worker';
import type { SerializedDepthState } from '../../../../utils/workers/shared-worker-types';
import { DepthSegmentationPipeline } from '../../../../utils/segmentation/DepthSegmentationCore';
import { PolygonProcessingPipeline } from '../../../../utils/PolygonProcessingPipeline';
import { PolygonProcessor } from '../../../../utils/PolygonProcessor';
import { WorkerClient, WorkerMessage, PendingRequest } from '../../../../utils/workers/WorkerClient';
import LineDetectionProcessorWorker from '../../../../utils/workers/line-detection-processor.worker?worker';
import SamplingPreviewWorker from '../../../../utils/workers/sampling-preview.worker?worker';

export interface ProcessResult {
  lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
  debugData: {
    capturedImage: ImageData;
    grayscaleImage: ImageData;
    edgesImage: ImageData;
    rawLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
    filteredLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
  };
  passTimings: Map<string, number>;
  autoPrompts?: DepthAutoPrompt[];
  regionGraph?: RegionGraph;
  showRegionGraph?: boolean;
  segmentationColoredData?: Uint8ClampedArray;
  segmentationWidth?: number;
  segmentationHeight?: number;
}

export type { DepthAutoPrompt, RegionGraph, ProcessedRegionPolygon };

/**
 * Preview mask result including resolved region ID for caching
 */
export interface PreviewMaskResult {
  imageData: ImageData;
  regionId: number;
}

/** Simple 3D point type for serializable world corners */
export interface WorldCorner {
  x: number;
  y: number;
  z: number;
}

/**
 * Rect preview result: combined mask + list of region IDs found in the rectangle brush
 */
export interface PreviewRectResult {
  imageData: ImageData;
  regionIds: number[];
  /** Clean region-only mask (no raw brush pixels). Present on paintRect responses. */
  cleanImageData?: ImageData;
  /** Full expanded set of selected region IDs (authoritative from worker). Present on paintRect responses. */
  allSelectedIds?: number[];
  /** World-space corners at the time of capture (for rendering in correct position) */
  worldCorners?: WorldCorner[];
}

export interface UpdateSelectionResult {
  imageData: ImageData | null;
  /** Full expanded set of selected region IDs (authoritative from worker). */
  allSelectedIds?: number[];
  /** World-space corners at the time of capture (for rendering in correct position) */
  worldCorners?: WorldCorner[];
}

/** Union of all possible pending request resolve value types */
type LineDetectionResolveValue = void | ProcessResult | ProcessedRegionPolygon | null | undefined;

/** Union of all possible sampling worker resolve value types */
type SamplingResolveValue = void | PreviewMaskResult | PreviewRectResult | UpdateSelectionResult | SegmentationResult | null | undefined;

/** Segmentation result shape returned by runSegmentation */
interface SegmentationResult {
  autoPrompts?: DepthAutoPrompt[];
  regionGraph?: RegionGraph;
  showRegionGraph?: boolean;
  depthPassTimings?: Record<string, number>;
  serializedDepthState?: SerializedDepthState;
  worldCorners?: WorldCorner[];
}

// ─── Line Detection Worker Client ────────────────────────────────────────────

class LineDetectionClient extends WorkerClient<LineDetectionResolveValue> {
  /** Reference to the parent manager's polygon pipeline for syncing timings */
  polygonPipeline: PolygonProcessingPipeline | null = null;

  constructor() {
    super(new LineDetectionProcessorWorker());
  }

  protected handleResponse(
    response: WorkerMessage,
    pending: PendingRequest<LineDetectionResolveValue>,
  ): void {
    const { type, data, error } = response as unknown as WorkerResponse;

    if (type === 'init_success') {
      this.isInitialized = true;
      pending.resolve(undefined);
    } else if (type === 'success' && data) {
      // Reconstruct ImageData from serialized data
      const passTimings = new Map<string, number>();
      if (data.passTimings) {
        for (const [key, value] of Object.entries(data.passTimings)) {
          passTimings.set(key, value);
        }
      }

      const result: ProcessResult = {
        lines: data.lines,
        debugData: {
          capturedImage: new ImageData(new Uint8ClampedArray(data.debugData.capturedImage.data), data.debugData.capturedImage.width, data.debugData.capturedImage.height),
          grayscaleImage: new ImageData(new Uint8ClampedArray(data.debugData.grayscaleImage.data), data.debugData.grayscaleImage.width, data.debugData.grayscaleImage.height),
          edgesImage: new ImageData(new Uint8ClampedArray(data.debugData.edgesImage.data), data.debugData.edgesImage.width, data.debugData.edgesImage.height),
          rawLines: data.debugData.rawLines,
          filteredLines: data.debugData.filteredLines,
        },
        passTimings,
      };
      pending.resolve(result);
    } else if (type === 'processRegion_success') {
      // Copy polygon pipeline timings from worker to main-thread pipeline
      if (this.polygonPipeline && data?.passTimings && Object.keys(data.passTimings).length > 0) {
        this.polygonPipeline.passTimings.clear();
        for (const [key, value] of Object.entries(data.passTimings)) {
          this.polygonPipeline.passTimings.set(key, value);
        }
      }
      pending.resolve(data?.processedPolygon ?? null);
    } else if (type === 'cancelled') {
      pending.reject(new DOMException('Processing cancelled', 'AbortError'));
    } else if (type === 'error') {
      pending.reject(new Error(error || 'Unknown worker error'));
    } else {
      pending.resolve(undefined);
    }
  }

  protected onWorkerError(error: ErrorEvent): void {
    console.error('[LineDetectionWorkerManager] Line detection worker error:', error);
    super.onWorkerError(error);
  }
}

// ─── Sampling Preview Worker Client ──────────────────────────────────────────

class SamplingPreviewClient extends WorkerClient<SamplingResolveValue> {
  constructor() {
    super(new SamplingPreviewWorker());
  }

  protected handleResponse(
    response: WorkerMessage,
    pending: PendingRequest<SamplingResolveValue>,
  ): void {
    const { type, data, error } = response as unknown as SamplingPreviewResponse;

    if (type === 'init_success') {
      this.isInitialized = true;
      if (pending.type === 'updateSelectedRegions' && data?.paintMask) {
        const pm = data.paintMask;
        const result: UpdateSelectionResult = {
          imageData: new ImageData(new Uint8ClampedArray(pm.data), pm.width, pm.height),
          allSelectedIds: data.allSelectedIds,
          worldCorners: data.worldCorners,
        };
        pending.resolve(result);
      } else if (pending.type === 'updateSelectedRegions') {
        pending.resolve({ imageData: null, worldCorners: data?.worldCorners } as UpdateSelectionResult);
      } else {
        pending.resolve(undefined);
      }
    } else if (type === 'segmentation_success' && data) {
      pending.resolve(data as unknown as SegmentationResult);
    } else if (type === 'preview_success') {
      const pm = data?.previewMask;
      const previewResult: PreviewMaskResult | null =
        pm && pm.width > 0
          ? {
              imageData: new ImageData(new Uint8ClampedArray(pm.data), pm.width, pm.height),
              regionId: pm.regionId,
            }
          : null;
      pending.resolve(previewResult);
    } else if (type === 'preview_rect_success') {
      const pm = data?.previewMask;
      const circleResult: PreviewRectResult | null =
        pm && pm.width > 0
          ? {
              imageData: new ImageData(new Uint8ClampedArray(pm.data), pm.width, pm.height),
              regionIds: data?.regionIds ?? [],
              worldCorners: data?.worldCorners,
            }
          : null;
      pending.resolve(circleResult);
    } else if (type === 'paint_rect_success') {
      const pm = data?.previewMask;
      const cm = data?.cleanMask;
      const paintResult: PreviewRectResult | null =
        pm && pm.width > 0
          ? {
              imageData: new ImageData(new Uint8ClampedArray(pm.data), pm.width, pm.height),
              regionIds: data?.regionIds ?? [],
              cleanImageData: cm && cm.width > 0 ? new ImageData(new Uint8ClampedArray(cm.data), cm.width, cm.height) : undefined,
              allSelectedIds: data?.allSelectedIds,
              worldCorners: data?.worldCorners,
            }
          : null;
      pending.resolve(paintResult);
    } else if (type === 'paint_rect_batch_success') {
      const pm = data?.previewMask;
      const batchResult: PreviewRectResult | null =
        pm && pm.width > 0
          ? {
              imageData: new ImageData(new Uint8ClampedArray(pm.data), pm.width, pm.height),
              regionIds: data?.regionIds ?? [],
              allSelectedIds: data?.allSelectedIds,
              worldCorners: data?.worldCorners,
            }
          : null;
      pending.resolve(batchResult);
    } else if (type === 'finalize_brush_success') {
      const pm = data?.previewMask;
      const cm = data?.cleanMask;
      const finalizeResult: PreviewRectResult | null =
        pm && pm.width > 0
          ? {
              imageData: new ImageData(new Uint8ClampedArray(pm.data), pm.width, pm.height),
              regionIds: [],
              cleanImageData: cm && cm.width > 0 ? new ImageData(new Uint8ClampedArray(cm.data), cm.width, cm.height) : undefined,
              allSelectedIds: data?.allSelectedIds,
              worldCorners: data?.worldCorners,
            }
          : null;
      pending.resolve(finalizeResult);
    } else if (type === 'error') {
      pending.reject(new Error(error || 'Unknown sampling worker error'));
    } else {
      pending.resolve(undefined);
    }
  }

  protected onWorkerError(error: ErrorEvent): void {
    console.error('[LineDetectionWorkerManager] Sampling preview worker error:', error);
    super.onWorkerError(error);
  }
}

// ─── Line Detection Worker Manager ──────────────────────────────────────────

/**
 * Manager for the line detection and sampling preview workers
 */
export class LineDetectionWorkerManager {
  // Line detection worker client
  private lineClient: LineDetectionClient;
  private cachedPipelineConfig: PipelineConfig<LineDetectionKwargs> | null = null;
  private cachedCmToPixelsRatio: number | null = null;

  // Sampling preview worker client
  private samplingClient: SamplingPreviewClient;

  /** Main-thread depth pipeline — used only for UI; config is synced to the sampling worker. */
  public readonly depthPipeline: DepthSegmentationPipeline;

  /** Main-thread polygon pipeline — used only for UI; config is synced to the line detection worker. */
  public readonly polygonPipeline: PolygonProcessingPipeline;

  /** Polygon processor that owns the pipeline and registers all processors */
  private readonly polygonProcessor: PolygonProcessor;

  constructor(polygonConfigPathOverride?: string, depthConfigPathOverride?: string) {
    // DepthSegmentationPipeline already registers all processors and sets up default passes
    this.depthPipeline = new DepthSegmentationPipeline();

    // Load config from file to override defaults. The path is overridable so
    // kind-specific registrations swap in a different depth pipeline — rooms
    // use the height/Z-tuned cluster-merge config, door/window the eye-space
    // wall config — without forking the worker.
    const depthConfigPath = depthConfigPathOverride ?? '/config/segmentation/line-tool-depth-pipeline-config.json';
    this.depthPipeline.loadFromFile(depthConfigPath).catch(() => {
      // Config file not found, keep the default pipeline passes
    });

    // PolygonProcessor registers all processors and creates the pipeline.
    // The config path is overridable so kind-specific LineTool registrations
    // (door/window strips, surfaces) can swap in a different polygon pipeline
    // — e.g. rectangles-only for openings — without forking the worker.
    const polygonConfigPath = polygonConfigPathOverride ?? '/config/segmentation/line-tool-poly-pipeline-config.json';
    this.polygonProcessor = new PolygonProcessor({
      skipAutoLoad: true, // We'll load config manually
      configPath: polygonConfigPath,
    });
    this.polygonPipeline = this.polygonProcessor.getPipeline();

    // Load polygon pipeline config from file
    this.polygonPipeline.loadFromFile(polygonConfigPath).catch(() => {
      // Config file not found, keep the default pipeline passes
    });

    // When pipeline configs load from file, sync to the respective workers (if already initialized)
    this.polygonPipeline.setOnPipelineLoaded(() => {
      this.updatePolygonPipeline();
    });
    this.depthPipeline.setOnPipelineLoaded(() => {
      this.updateDepthPipeline();
    });

    this.lineClient = new LineDetectionClient();
    this.lineClient.polygonPipeline = this.polygonPipeline;
    this.samplingClient = new SamplingPreviewClient();
  }

  // ─── Initialization ────────────────────────────────────────────────────

  /**
   * Initialize the line detection worker with pipeline config
   */
  private async initWorker(): Promise<void> {
    // Wait for pipeline config to load from file before initializing
    await this.polygonPipeline.waitForLoad();

    return this.lineClient.sendRequest<void>('init', {
      pipelineConfig: this.cachedPipelineConfig ?? undefined,
      polygonPipelineConfig: this.polygonPipeline.getConfig() as PipelineConfig,
      cmToPixelsRatio: this.cachedCmToPixelsRatio,
    });
  }

  /**
   * Initialize the sampling preview worker with depth pipeline config
   */
  private async initSamplingWorker(): Promise<void> {
    // Wait for depth pipeline config to load from file before initializing
    await this.depthPipeline.waitForLoad();

    return this.samplingClient.sendRequest<void>('init', {
      depthPipelineConfig: this.depthPipeline.getConfig() as PipelineConfig,
    });
  }

  // ─── Line detection operations ─────────────────────────────────────────

  /**
   * Cancel any ongoing processing by sending abort signal to the line detection worker
   * The worker stays alive and ready for the next request
   */
  cancel(): void {
    if (!this.lineClient.worker) {
      return;
    }

    // Send cancel message to abort ongoing processing (fire and forget)
    this.lineClient.worker.postMessage({ id: `cancel_${Date.now()}`, type: 'cancel' });

    // Reject all pending process requests with abort error
    this.lineClient.pendingRequests.forEach(({ reject, type }, requestId) => {
      if (type === 'process') {
        reject(new DOMException('Processing cancelled', 'AbortError'));
        this.lineClient.pendingRequests.delete(requestId);
      }
    });
  }

  /**
   * Process image data through the line detection pipeline.
   * Also runs depth segmentation on the sampling worker if a position map is provided.
   */
  async process(imageData: ImageData, pipelineConfigJson: string, cmToPixelsRatio: number | null, positionMap?: ImageData | null, excludePolygons?: Array<Array<{ x: number; y: number }>>): Promise<ProcessResult> {
    // Parse and cache the pipeline config
    try {
      this.cachedPipelineConfig = JSON.parse(pipelineConfigJson);
    } catch {
      // Use default if parsing fails
    }
    this.cachedCmToPixelsRatio = cmToPixelsRatio;

    // Ensure both workers are initialized
    if (!this.lineClient.isInitialized) {
      await this.initWorker();
    } else {
      await this.updatePipelineConfig();
      await this.updateCmToPixelsRatio();
    }

    if (!this.samplingClient.isInitialized) {
      await this.initSamplingWorker();
    }

    // Run line detection and depth segmentation in parallel on separate workers.
    // The density image doubles as the x-ray gate for remove_invalid_areas.
    const lineDetectionPromise = this.runLineDetection(imageData, positionMap ?? undefined);
    const segmentationPromise = positionMap ? this.runSegmentation(positionMap, undefined, imageData, excludePolygons) : Promise.resolve(null);

    const [lineResult, segmentationResult] = await Promise.all([lineDetectionPromise, segmentationPromise]);

    // Forward depth state to line detection worker for processRegion
    if (segmentationResult?.serializedDepthState) {
      this.forwardDepthStateToLineWorker(segmentationResult.serializedDepthState, segmentationResult.worldCorners);
    }

    // Merge depth pipeline timings into main-thread depth pipeline for UI
    if (segmentationResult?.depthPassTimings) {
      this.depthPipeline.passTimings.clear();
      for (const [key, value] of Object.entries(segmentationResult.depthPassTimings)) {
        this.depthPipeline.passTimings.set(key, value);
      }
    }

    // Combine results
    return {
      ...lineResult,
      autoPrompts: segmentationResult?.autoPrompts,
      regionGraph: segmentationResult?.regionGraph ?? undefined,
      showRegionGraph: segmentationResult?.showRegionGraph ?? false,
      segmentationColoredData: segmentationResult?.serializedDepthState?.coloredData,
      segmentationWidth: segmentationResult?.serializedDepthState?.width,
      segmentationHeight: segmentationResult?.serializedDepthState?.height,
    };
  }

  /**
   * Run line detection only on the line detection worker
   */
  private async runLineDetection(imageData: ImageData, positionMap?: ImageData): Promise<ProcessResult> {
    return this.lineClient.sendRequest<ProcessResult>('process', {
      imageData: {
        data: imageData.data,
        width: imageData.width,
        height: imageData.height,
      },
      positionMap: positionMap ? { data: positionMap.data, width: positionMap.width, height: positionMap.height } : undefined,
    });
  }

  /**
   * Run only depth segmentation (no line detection).
   * Use when no line prompts exist and Ctrl is not pressed.
   */
  async processSegmentationOnly(
    positionMap: ImageData,
    worldCorners?: WorldCorner[],
    xray?: ImageData | null,
    excludePolygons?: Array<Array<{ x: number; y: number }>>,
  ): Promise<{
    autoPrompts?: DepthAutoPrompt[];
    regionGraph?: RegionGraph;
    showRegionGraph?: boolean;
    segmentationColoredData?: Uint8ClampedArray;
    segmentationWidth?: number;
    segmentationHeight?: number;
  }> {
    // Initialize both workers in parallel if needed (line worker needed for processRegion later)
    const initPromises: Promise<void>[] = [];
    if (!this.samplingClient.isInitialized) {
      initPromises.push(this.initSamplingWorker());
    }
    if (!this.lineClient.isInitialized) {
      initPromises.push(this.initWorker());
    }
    if (initPromises.length > 0) {
      await Promise.all(initPromises);
    }

    const segmentationResult = await this.runSegmentation(positionMap, worldCorners, xray, excludePolygons);

    // Forward depth state to line detection worker for processRegion
    if (segmentationResult?.serializedDepthState) {
      this.forwardDepthStateToLineWorker(segmentationResult.serializedDepthState, segmentationResult.worldCorners);
    }

    // Merge depth pipeline timings into main-thread depth pipeline for UI
    if (segmentationResult?.depthPassTimings) {
      this.depthPipeline.passTimings.clear();
      for (const [key, value] of Object.entries(segmentationResult.depthPassTimings)) {
        this.depthPipeline.passTimings.set(key, value);
      }
    }

    return {
      autoPrompts: segmentationResult?.autoPrompts,
      regionGraph: segmentationResult?.regionGraph ?? undefined,
      showRegionGraph: segmentationResult?.showRegionGraph ?? false,
      segmentationColoredData: segmentationResult?.serializedDepthState?.coloredData,
      segmentationWidth: segmentationResult?.serializedDepthState?.width,
      segmentationHeight: segmentationResult?.serializedDepthState?.height,
    };
  }

  /**
   * Run only line detection (no depth segmentation).
   * Use when line prompts exist or Ctrl is pressed.
   */
  async processLinesOnly(imageData: ImageData, pipelineConfigJson: string, cmToPixelsRatio: number | null, positionMap?: ImageData | null): Promise<ProcessResult> {
    // Parse and cache the pipeline config
    try {
      this.cachedPipelineConfig = JSON.parse(pipelineConfigJson);
    } catch {
      // Use default if parsing fails
    }
    this.cachedCmToPixelsRatio = cmToPixelsRatio;

    // Ensure line detection worker is initialized
    if (!this.lineClient.isInitialized) {
      await this.initWorker();
    } else {
      await this.updatePipelineConfig();
      await this.updateCmToPixelsRatio();
    }

    // Run line detection only (no segmentation)
    const lineResult = await this.runLineDetection(imageData, positionMap ?? undefined);

    return {
      ...lineResult,
      autoPrompts: undefined,
      regionGraph: undefined,
      showRegionGraph: false,
      segmentationColoredData: undefined,
      segmentationWidth: undefined,
      segmentationHeight: undefined,
    };
  }

  /**
   * Run segmentation on the sampling worker
   */
  private async runSegmentation(
    positionMap: ImageData,
    worldCorners?: WorldCorner[],
    xray?: ImageData | null,
    excludePolygons?: Array<Array<{ x: number; y: number }>>,
  ): Promise<SegmentationResult | null> {
    if (!this.samplingClient.worker) return null;

    return this.samplingClient.sendRequest<SegmentationResult>('runSegmentation', {
      positionMap: {
        data: positionMap.data,
        width: positionMap.width,
        height: positionMap.height,
      },
      worldCorners: worldCorners,
      xray: xray ? { data: xray.data, width: xray.width, height: xray.height } : undefined,
      excludePolygons: excludePolygons && excludePolygons.length > 0 ? excludePolygons : undefined,
    });
  }

  /**
   * Forward depth state to the line detection worker (for processRegion).
   * Fire-and-forget — we don't need to wait for acknowledgement.
   */
  private forwardDepthStateToLineWorker(serializedDepthState: SerializedDepthState, worldCorners?: WorldCorner[]): void {
    if (!this.lineClient.worker) return;

    // Fire-and-forget: send directly via postMessage, no pending tracking needed
    const id = `setDepthState_${Date.now()}`;
    this.lineClient.worker.postMessage({
      id,
      type: 'setDepthState',
      data: { depthState: serializedDepthState, worldCorners },
    } satisfies WorkerRequest);
  }

  // ─── Pipeline config updates ───────────────────────────────────────────

  /**
   * Update the pipeline configuration in the line detection worker
   */
  private async updatePipelineConfig(): Promise<void> {
    const config = this.cachedPipelineConfig;
    if (!this.lineClient.worker || !config) {
      return;
    }

    return this.lineClient.sendRequest<void>('updatePipeline', {
      pipelineConfig: config,
    });
  }

  /**
   * Update the cm to pixels ratio in the line detection worker
   */
  private async updateCmToPixelsRatio(): Promise<void> {
    if (!this.lineClient.worker) {
      return;
    }

    return this.lineClient.sendRequest<void>('updateCmToPixels', {
      cmToPixelsRatio: this.cachedCmToPixelsRatio,
    });
  }

  /**
   * Sync the current depth pipeline config to the sampling preview worker.
   * Call this after making UI changes to depthPipeline.
   */
  async updateDepthPipeline(): Promise<void> {
    if (!this.samplingClient.worker || !this.samplingClient.isInitialized) return;

    return this.samplingClient.sendRequest<void>('updateDepthPipeline', {
      depthPipelineConfig: this.depthPipeline.getConfig() as PipelineConfig,
    });
  }

  /**
   * Sync the current polygon pipeline config to the line detection worker.
   * Call this after making UI changes to polygonPipeline.
   */
  async updatePolygonPipeline(): Promise<void> {
    if (!this.lineClient.worker || !this.lineClient.isInitialized) return;

    return this.lineClient.sendRequest<void>('updatePolygonPipeline', {
      polygonPipelineConfig: this.polygonPipeline.getConfig() as PipelineConfig,
    });
  }

  // ─── Selection and preview (routed to sampling worker) ─────────────────

  /**
   * Update the cached selected regions in both workers.
   * Call this when the selection changes to pre-compute the selection mask.
   */
  async updateSelectedRegions(selectedRegionIds: number[]): Promise<UpdateSelectionResult> {
    let samplingPromise: Promise<UpdateSelectionResult> = Promise.resolve({ imageData: null });
    const otherPromises: Promise<void>[] = [];

    // Update sampling worker (for preview mask)
    if (this.samplingClient.worker && this.samplingClient.isInitialized) {
      samplingPromise = this.samplingClient.sendRequest<UpdateSelectionResult>('updateSelectedRegions', {
        selectedRegionIds,
      });
    }

    // Update line detection worker (for processRegion mask)
    if (this.lineClient.worker && this.lineClient.isInitialized) {
      otherPromises.push(
        this.lineClient.sendRequest<void>('updateSelectedRegions', {
          selectedRegionIds,
        }),
      );
    }

    const [paintMask] = await Promise.all([samplingPromise, ...otherPromises]);
    return paintMask;
  }

  /**
   * Get a hover preview mask for the region at the given normalised position.
   * Routed to the dedicated sampling preview worker for fast response.
   * Returns null if no depth state is cached yet.
   */
  async preview(normalizedX: number, normalizedY: number): Promise<PreviewMaskResult | null> {
    if (!this.samplingClient.worker || !this.samplingClient.isInitialized) return null;

    return this.samplingClient.sendRequest<PreviewMaskResult | null>('preview', {
      normalizedX,
      normalizedY,
    });
  }

  /**
   * Get a circle preview: all regions within a pixel radius of the given position.
   * Returns combined mask + list of region IDs found.
   */
  async previewRect(normalizedX: number, normalizedY: number, halfSizePixels: number): Promise<PreviewRectResult | null> {
    if (!this.samplingClient.worker || !this.samplingClient.isInitialized) return null;

    return this.samplingClient.sendRequest<PreviewRectResult | null>('previewRect', {
      normalizedX,
      normalizedY,
      halfSizePixels,
    });
  }

  /**
   * Paint a rectangle directly into the worker's selection mask and return
   * the updated mask + any new region IDs found in the rectangle.
   */
  async paintRect(normalizedX: number, normalizedY: number, halfSizePixels: number): Promise<PreviewRectResult | null> {
    if (!this.samplingClient.worker || !this.samplingClient.isInitialized) return null;

    return this.samplingClient.sendRequest<PreviewRectResult | null>('paintRect', {
      normalizedX,
      normalizedY,
      halfSizePixels,
    });
  }

  /**
   * Paint multiple brush rectangles in one batch (fast path — no expandRegions).
   * Returns the accumulated display mask + direct region hits.
   */
  async paintRectBatch(positions: Array<{ normalizedX: number; normalizedY: number; halfSizePixels: number }>): Promise<PreviewRectResult | null> {
    if (!this.samplingClient.worker || !this.samplingClient.isInitialized || positions.length === 0) return null;

    return this.samplingClient.sendRequest<PreviewRectResult | null>('paintRectBatch', {
      positions,
    });
  }

  /**
   * Finalize the brush selection: run expandRegions + build clean mask.
   * Called once on mouse-up after batched painting.
   */
  async finalizeBrushSelection(): Promise<PreviewRectResult | null> {
    if (!this.samplingClient.worker || !this.samplingClient.isInitialized) return null;

    return this.samplingClient.sendRequest<PreviewRectResult | null>('finalizeBrush', undefined);
  }

  // ─── Polygon processing (routed to line detection worker) ──────────────

  /**
   * Process a region at the given position into a polygon using the full PolygonProcessor pipeline.
   * Returns the processed polygon in pixel space, or null if no region found.
   * Uses depth state previously forwarded via setDepthState.
   */
  async processRegion(normalizedX: number, normalizedY: number, pixelsPerMeter: number, positionMap?: ImageData | null, densityMap?: ImageData | null): Promise<ProcessedRegionPolygon | null> {
    if (!this.lineClient.worker || !this.lineClient.isInitialized) return null;

    return this.lineClient.sendRequest<ProcessedRegionPolygon | null>('processRegion', {
      normalizedX,
      normalizedY,
      pixelsPerMeter,
      positionMap: positionMap ? { data: positionMap.data, width: positionMap.width, height: positionMap.height } : undefined,
      densityMap: densityMap ? { data: densityMap.data, width: densityMap.width, height: densityMap.height } : undefined,
    });
  }

  /**
   * Process a pre-built mask into a polygon.
   * The mask comes from the sampling worker (already includes selection + hover).
   * Avoids the line-detection worker needing to rebuild the mask from depth state.
   */
  async processRegionFromMask(
    maskData: Uint8ClampedArray,
    maskWidth: number,
    maskHeight: number,
    resolvedRegionId: number,
    pixelsPerMeter: number,
    positionMap?: ImageData | null,
    densityMap?: ImageData | null,
  ): Promise<ProcessedRegionPolygon | null> {
    if (!this.lineClient.worker || !this.lineClient.isInitialized) return null;

    return this.lineClient.sendRequest<ProcessedRegionPolygon | null>('processRegionFromMask', {
      maskData,
      maskWidth,
      maskHeight,
      resolvedRegionId,
      pixelsPerMeter,
      positionMap: positionMap ? { data: positionMap.data, width: positionMap.width, height: positionMap.height } : undefined,
      densityMap: densityMap ? { data: densityMap.data, width: densityMap.width, height: densityMap.height } : undefined,
    });
  }

  // ─── Cleanup ───────────────────────────────────────────────────────────

  /**
   * Clean up resources
   */
  dispose(): void {
    // Resolve pending requests with null instead of rejecting —
    // callers already handle null results, and rejecting causes
    // uncaught promise errors during normal teardown.
    this.lineClient.pendingRequests.forEach(({ resolve }) => {
      resolve(null);
    });
    this.lineClient.pendingRequests.clear();
    this.lineClient.terminate();

    this.samplingClient.pendingRequests.forEach(({ resolve }) => {
      resolve(null);
    });
    this.samplingClient.pendingRequests.clear();
    this.samplingClient.terminate();
  }
}
